import { DialogTitle } from "@headlessui/react";
import { CloudArrowUpIcon } from "@heroicons/react/24/outline";
import Button from "components/buttons/Button";
import SwitchButton from "components/buttons/SwitchButton";
import Modal from "components/modal/Modal";
import { toastContent } from "components/toastContent";
import { t } from "i18next";
import type { TaskFileUploadType } from "pages/project/common/taskBase.typing";
import { validateFile } from "pages/project/task/components/modals/fileUploadValidation";
import { useManagedFilesToBeUploaded } from "pages/project/task/components/modals/useManagedFilesToBeUploaded";
import { useDeliverTaskMutation } from "query/task.query";
import { useState, useEffect, useRef, useCallback } from "react";
import type { FileRejection } from "react-dropzone";
import { useDropzone } from "react-dropzone";
import { toast } from "react-hot-toast";
import { Trans } from "react-i18next";
import { useFileUploadStore } from "store/useFileUploadStore";
import { bytesToSize } from "utils/format";
import { PortalUploadModeEnum } from "api/api.typing";
import { PreprocessingFileMappingTable } from "./preprocessingMapping/PreprocessingFileMappingTable";
import { UploadedFileList } from "./UploadedFileList";
import { useNewFileDetection } from "./useNewFileDetection";
import { usePreprocessingFileMapping } from "./usePreprocessingFileMapping";
import TextAreaFormField from "components/form/TextAreaFormField";
import { COMMENT_MAX_CHAR_LIMIT } from "./useDeliverTaskForm";
import { useForm } from "react-hook-form";

interface Props {
  taskId: string;
  catToolId: number;
  taskCode: string;
  onClose: () => void;
  canDeliver: boolean;
  isUserSupplier: boolean;
  fileType: TaskFileUploadType;
  portalUploadMode: PortalUploadModeEnum;
}

const MAX_FILE_SIZE = 2_000_000_000; // 2 GB

const onFileDropRejected = (rejected: FileRejection[]) => {
  for (const file of rejected) {
    const error = t(`task.upload.errors.${file.errors[0].code}`);
    const errorDetail = file.errors[0].message;
    toast.error(toastContent(t("task.upload.toaster-title"), `${file.file.name}\r\n${errorDetail ?? error}`));
  }
};

const UploadTaskMaterialModal = ({
  taskId,
  taskCode,
  onClose,
  canDeliver,
  catToolId,
  isUserSupplier,
  fileType,
  portalUploadMode,
}: Props) => {
  const [isTaskDelivered, toggleTaskDelivered] = useState<boolean>(false);
  const [areFileMappingsValid, setAreFileMappingsValid] = useState<boolean>(false);
  const [fileMappings, setFileMappings] = useState<Map<string, { sourceFileIds?: number[]; languageCodes: string[] }>>(
    new Map()
  );
  const { register, formState, getValues } = useForm<{
    comment?: string;
  }>();

  // Track fetched file names to prevent race conditions
  const fetchedFilesRef = useRef<Set<string>>(new Set());

  // Stabilize callbacks to prevent unnecessary re-renders in child components
  const handleValidationChange = useCallback((isValid: boolean) => {
    setAreFileMappingsValid(isValid);
  }, []);

  const handleMappingsChange = useCallback(
    (mappings: Map<string, { sourceFileIds?: number[]; languageCodes: string[] }>) => {
      setFileMappings(mappings);
    },
    []
  );

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    onDropRejected: onFileDropRejected,
    validator: validateFile,
    multiple: true,
    maxSize: MAX_FILE_SIZE,
  });

  const title = t(fileType === "delivery" ? "task.upload.title" : "task.upload-notes.title");

  const { deliverTask } = useDeliverTaskMutation(taskId, catToolId, isUserSupplier);
  const upload = useFileUploadStore((state) => state.upload);

  const { files, removeFile } = useManagedFilesToBeUploaded(acceptedFiles);

  // Detect newly added files
  const { newFileNames, clearNewFiles } = useNewFileDetection(files);

  // Handle preprocessing file mapping
  const { shouldShowSection, sourceFiles, targetLanguages, fileMappingSuggestions } = usePreprocessingFileMapping(
    taskId,
    fileType,
    newFileNames
  );

  // Enlarge modal when file mapping section is shown
  const modalWidth = shouldShowSection ? "sm:max-w-7xl" : "sm:max-w-lg";

  // Clear newFileNames after suggestions are fetched to prevent refetching
  useEffect(() => {
    if (fileMappingSuggestions.length > 0 && newFileNames.length > 0) {
      // Check if these suggestions are for new files
      const hasNewSuggestions = newFileNames.some((name) => !fetchedFilesRef.current.has(name));

      if (hasNewSuggestions) {
        // Mark these files as fetched
        newFileNames.forEach((name) => fetchedFilesRef.current.add(name));
        clearNewFiles();
      }
    }
  }, [fileMappingSuggestions, newFileNames, clearNewFiles]);

  const handleCancel = () => {
    if (files.length > 0) {
      toast(t("task.upload.upload-canceled"));
    }
    onClose();
  };

  const handleUpload = () => {
    onClose();
    upload(
      taskId,
      taskCode,
      isTaskDelivered,
      files,
      fileType,
      deliverTask,
      portalUploadMode,
      fileMappings,
      getValues("comment")
    );
  };

  return (
    <Modal isShown={true} maxWidthClassName={modalWidth}>
      <div className="mt-3 text-center sm:mt-5">
        <DialogTitle as="h3" className="text-color-primary text-lg font-medium leading-6 sm:text-3xl">
          <div>{title}</div>
          <div>{taskCode}</div>
        </DialogTitle>
      </div>

      <div className="mx-4 mt-5">
        <div className="flex w-full items-center justify-center">
          <div
            className="flex h-56 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:hover:border-gray-500 dark:hover:bg-gray-800"
            {...getRootProps()}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center justify-center pb-6 pt-5">
              <CloudArrowUpIcon className="size-10 text-gray-500 dark:text-gray-400" />
              {isDragActive ? (
                <p className="text-color-secondary mb-2 text-sm">{t("task.upload.upload-message-active") as string}</p>
              ) : (
                <>
                  <p className="text-color-secondary mb-2 text-sm">
                    <Trans
                      i18nKey="task.upload.upload-message"
                      components={{
                        s: <span className="font-semibold" />,
                      }}
                    />
                  </p>
                  <p className="text-color-secondary text-xs">ZIP, PDF, DOC,... (MAX. {bytesToSize(MAX_FILE_SIZE)})</p>
                </>
              )}
            </div>
          </div>
        </div>

        {files.length > 0 && (
          <>
            <UploadedFileList files={files} onRemove={removeFile} />

            {/*Preprocessing File Mapping (Title + Banner + Toolbar + Grid)*/}
            {shouldShowSection && (
              <PreprocessingFileMappingTable
                files={files}
                sourceFiles={sourceFiles}
                targetLanguages={targetLanguages}
                fileMappingSuggestions={fileMappingSuggestions}
                onValidationChange={handleValidationChange}
                onMappingsChange={handleMappingsChange}
              />
            )}
          </>
        )}
        {canDeliver && (
          <div className="mt-5 border-t border-slate-200 pt-5 dark:border-slate-700">
            <SwitchButton
              checked={isTaskDelivered}
              toggleCheck={toggleTaskDelivered}
              label={t(isUserSupplier ? "task.upload.mark-delivered" : "task.upload.mark-validated")}
            />
            {isTaskDelivered && (
              <div className="mt-2">
                <TextAreaFormField
                  id="comment"
                  label={t("task.upload.form.comment")}
                  className="max-h-52"
                  {...register("comment")}
                  error={formState.errors.comment?.message}
                  maxCharLimit={COMMENT_MAX_CHAR_LIMIT}
                  rows={3}
                />
              </div>
            )}
          </div>
        )}
        <div className="mt-5 grid grid-cols-2 gap-3 sm:mt-6 sm:grid-flow-row-dense">
          <Button type="button" color="ternary" label={t("task.upload.btn-cancel")} onClick={() => handleCancel()} />
          <Button
            type="button"
            color="primary"
            label={t("task.upload.btn-upload")}
            disabled={files.length === 0 || (shouldShowSection && !areFileMappingsValid)}
            onClick={handleUpload}
          />
        </div>
      </div>
    </Modal>
  );
};
export default UploadTaskMaterialModal;
